import { z } from 'zod';
import {
  SignalOriginSchema,
  type SignalOriginType,
} from '@terra/shared-schemas-orchestrator';
import {
  INTEGRATION_TYPES,
  getIntegrationDisplayName,
  resolveIntegrationTypeFromLabel,
} from '@terra/shared-contracts-integrations';

export type SignalOriginFilterValue = Exclude<
  SignalOriginType,
  'MANUAL' | 'SCANNER_IMPORT'
>;

export const SIGNAL_ORIGIN_FILTER_VALUES: readonly SignalOriginFilterValue[] =
  SignalOriginSchema.options.filter(
    (origin): origin is SignalOriginFilterValue =>
      origin !== SignalOriginSchema.enum.MANUAL &&
      origin !== SignalOriginSchema.enum.SCANNER_IMPORT,
  );

export const EXTERNAL_NETWORK_SIGNAL_ORIGIN_FILTER_VALUES = [
  SignalOriginSchema.enum.EXTERNAL_NETWORK_DISCOVERY,
  SignalOriginSchema.enum.ACTIVE_SCAN,
  SignalOriginSchema.enum.CVE_DATABASE,
  SignalOriginSchema.enum.EXTERNAL_INTEGRATION,
] as const satisfies readonly SignalOriginFilterValue[];

export type ExternalNetworkSignalOriginFilterValue =
  (typeof EXTERNAL_NETWORK_SIGNAL_ORIGIN_FILTER_VALUES)[number];

const EXTERNAL_NETWORK_SIGNAL_ORIGIN_FILTER_SET = new Set<string>(
  EXTERNAL_NETWORK_SIGNAL_ORIGIN_FILTER_VALUES,
);

export function isExternalNetworkSignalOriginFilterValue(
  value: string,
): value is ExternalNetworkSignalOriginFilterValue {
  return EXTERNAL_NETWORK_SIGNAL_ORIGIN_FILTER_SET.has(value);
}

export const INTERNAL_NETWORK_SIGNAL_ORIGIN_FILTER_VALUES = [
  SignalOriginSchema.enum.EXTERNAL_NETWORK_DISCOVERY,
  SignalOriginSchema.enum.CVE_DATABASE,
  SignalOriginSchema.enum.EXTERNAL_INTEGRATION,
] as const satisfies readonly SignalOriginFilterValue[];

export type InternalNetworkSignalOriginFilterValue =
  (typeof INTERNAL_NETWORK_SIGNAL_ORIGIN_FILTER_VALUES)[number];

const INTERNAL_NETWORK_SIGNAL_ORIGIN_FILTER_SET = new Set<string>(
  INTERNAL_NETWORK_SIGNAL_ORIGIN_FILTER_VALUES,
);

export function isInternalNetworkSignalOriginFilterValue(
  value: string,
): value is InternalNetworkSignalOriginFilterValue {
  return INTERNAL_NETWORK_SIGNAL_ORIGIN_FILTER_SET.has(value);
}

export const THIRD_PARTY_SIGNAL_ORIGINS = [
  SignalOriginSchema.enum.EXTERNAL_INTEGRATION,
  SignalOriginSchema.enum.SCANNER_IMPORT,
] as const satisfies readonly SignalOriginType[];

const THIRD_PARTY_SIGNAL_ORIGIN_SET = new Set<SignalOriginType>(
  THIRD_PARTY_SIGNAL_ORIGINS,
);

export const isThirdPartySignalOrigin = (origin: SignalOriginType): boolean =>
  THIRD_PARTY_SIGNAL_ORIGIN_SET.has(origin);

export const SignalSourceTypeSchema = z.enum(['native', 'third_party']);
export type SignalSourceType = z.infer<typeof SignalSourceTypeSchema>;

export const SIGNAL_SOURCE_IDS = {
  TERRA: 'terra',
  WIZ: INTEGRATION_TYPES.WIZ,
  UPWIND: INTEGRATION_TYPES.UPWIND,
  BUGCROWD: INTEGRATION_TYPES.BUGCROWD,
  HACKERONE: INTEGRATION_TYPES.HACKERONE,
  NOVA: INTEGRATION_TYPES.NOVA,
  FILE_IMPORT: 'file_import',
} as const;

export const SIGNAL_SOURCE_FILTER_VALUES = [
  SIGNAL_SOURCE_IDS.TERRA,
  SIGNAL_SOURCE_IDS.WIZ,
  SIGNAL_SOURCE_IDS.UPWIND,
  SIGNAL_SOURCE_IDS.BUGCROWD,
  SIGNAL_SOURCE_IDS.HACKERONE,
  SIGNAL_SOURCE_IDS.NOVA,
  SIGNAL_SOURCE_IDS.FILE_IMPORT,
] as const;

export const SignalSourceFilterValueSchema = z.enum(
  SIGNAL_SOURCE_FILTER_VALUES,
);
export type SignalSourceFilterValue = z.infer<
  typeof SignalSourceFilterValueSchema
>;

export const SignalSourceSchema = z.object({
  sourceType: SignalSourceTypeSchema,
  sourceId: z.string(),
  sourceName: z.string(),
  provider: z.string().nullable(),
  sourceLabel: z.string().nullable(),
});
export type SignalSource = z.infer<typeof SignalSourceSchema>;

const NATIVE_SOURCE_NAME = 'Terra';
const CO_PILOT_SOURCE_NAME = 'Co-Pilot';
const FILE_IMPORT_SOURCE_NAME = 'File Import';
const EXTERNAL_INTEGRATION_FALLBACK_SOURCE_NAME = 'External Integration';
const MISCONFIGURATION_RESEARCHER_FALLBACK_SOURCE_NAME =
  'Misconfiguration Researcher';
const EXTERNAL_INTEGRATION_FALLBACK_SOURCE_ID = 'external_integration';

const NATIVE_ORIGIN_VALUES = SignalOriginSchema.options.filter(
  (origin) => !isThirdPartySignalOrigin(origin),
);

type DeriveSignalSourceNameParams = {
  provider: string | null;
  sourceLabel: string | null;
};

const SOURCE_NAME_RESOLVERS: Partial<
  Record<SignalOriginType, (params: DeriveSignalSourceNameParams) => string>
> = {
  [SignalOriginSchema.enum.CO_PILOT]: () => CO_PILOT_SOURCE_NAME,
  [SignalOriginSchema.enum.EXTERNAL_INTEGRATION]: ({ provider }) => {
    if (provider === SIGNAL_SOURCE_IDS.FILE_IMPORT) {
      return FILE_IMPORT_SOURCE_NAME;
    }

    return provider
      ? getIntegrationDisplayName(provider)
      : EXTERNAL_INTEGRATION_FALLBACK_SOURCE_NAME;
  },
  [SignalOriginSchema.enum.MISCONFIGURATION_RESEARCHER]: ({
    provider,
    sourceLabel,
  }) =>
    provider
      ? getIntegrationDisplayName(provider)
      : (sourceLabel ?? MISCONFIGURATION_RESEARCHER_FALLBACK_SOURCE_NAME),
  [SignalOriginSchema.enum.SCANNER_IMPORT]: () => FILE_IMPORT_SOURCE_NAME,
};

const resolveSourceId = ({
  origin,
  provider,
}: {
  origin: SignalOriginType;
  provider: string | null;
}): string => {
  if (origin === SignalOriginSchema.enum.SCANNER_IMPORT) {
    return SIGNAL_SOURCE_IDS.FILE_IMPORT;
  }

  if (origin === SignalOriginSchema.enum.EXTERNAL_INTEGRATION) {
    return provider ?? EXTERNAL_INTEGRATION_FALLBACK_SOURCE_ID;
  }

  return SIGNAL_SOURCE_IDS.TERRA;
};

export type DeriveSignalSourceParams = {
  origin: SignalOriginType;
  provider: string | null;
  sourceLabel: string | null;
};

export const deriveSignalSource = ({
  origin,
  provider,
  sourceLabel,
}: DeriveSignalSourceParams): SignalSource => {
  const sourceType: SignalSourceType = isThirdPartySignalOrigin(origin)
    ? 'third_party'
    : 'native';
  const resolveSourceName = SOURCE_NAME_RESOLVERS[origin];
  const sourceName = resolveSourceName
    ? resolveSourceName({ provider, sourceLabel })
    : NATIVE_SOURCE_NAME;

  return {
    sourceType,
    sourceId: resolveSourceId({ origin, provider }),
    sourceName,
    provider,
    sourceLabel,
  };
};

export const deriveSignalSourceFromOrigin = (
  origin: SignalOriginType,
): SignalSource =>
  deriveSignalSource({ origin, provider: null, sourceLabel: null });

export type ImportBatchSourceFields = {
  origin: SignalOriginType | null;
  sourceLabel: string;
};

export const deriveImportBatchSignalSource = ({
  origin,
  sourceLabel,
  metadataProvider,
}: ImportBatchSourceFields & {
  metadataProvider?: string | null;
}): SignalSource => {
  const provider =
    metadataProvider ?? resolveIntegrationTypeFromLabel(sourceLabel);

  if (provider) {
    return deriveSignalSource({
      origin: SignalOriginSchema.enum.EXTERNAL_INTEGRATION,
      provider,
      sourceLabel,
    });
  }

  return deriveSignalSource({
    origin: origin ?? SignalOriginSchema.enum.SCANNER_IMPORT,
    provider: null,
    sourceLabel,
  });
};

export const isFileImportSource = (source: SignalSource): boolean =>
  source.sourceId === SIGNAL_SOURCE_IDS.FILE_IMPORT;

export const isNativeProductSignalSource = (source: SignalSource): boolean =>
  source.sourceType === 'native' && source.sourceName === NATIVE_SOURCE_NAME;

/**
 * True when an `EXTERNAL_INTEGRATION` signal is missing `metadata.provider`,
 * meaning `deriveSignalSource` fell back to a generic source name. Callers
 * with logger access should warn when this is true (see
 * `errors-and-logging.mdc`); this pure util cannot log itself.
 */
export const isMissingExternalIntegrationProvider = ({
  origin,
  provider,
}: Pick<DeriveSignalSourceParams, 'origin' | 'provider'>): boolean =>
  origin === SignalOriginSchema.enum.EXTERNAL_INTEGRATION && !provider;

export const isExternalIntegrationSourceFilterValue = (
  source: SignalSourceFilterValue,
): boolean =>
  source !== SIGNAL_SOURCE_IDS.TERRA &&
  source !== SIGNAL_SOURCE_IDS.FILE_IMPORT;

export const resolvePersistedSignalSourceProvider = ({
  origin,
  metadataProvider,
}: {
  origin: SignalOriginType;
  metadataProvider: string | null;
}): string | null => {
  if (origin === SignalOriginSchema.enum.SCANNER_IMPORT) {
    return SIGNAL_SOURCE_IDS.FILE_IMPORT;
  }

  if (origin === SignalOriginSchema.enum.EXTERNAL_INTEGRATION) {
    return metadataProvider;
  }

  return null;
};

export const buildSignalSourceWhere = ({
  sources,
}: {
  sources: readonly SignalSourceFilterValue[];
}): Record<string, unknown> | null => {
  if (!sources.includes(SIGNAL_SOURCE_IDS.TERRA)) {
    return null;
  }

  return { origin: { in: NATIVE_ORIGIN_VALUES } };
};

export const buildSignalSourceProviderWhere = ({
  sources,
}: {
  sources: readonly SignalSourceFilterValue[];
}): Record<string, unknown> | null => {
  const thirdPartySources = sources.filter(
    (source) => source !== SIGNAL_SOURCE_IDS.TERRA,
  );

  if (thirdPartySources.length === 0) {
    return null;
  }

  const clauses: Record<string, unknown>[] = [];

  if (thirdPartySources.includes(SIGNAL_SOURCE_IDS.FILE_IMPORT)) {
    clauses.push({ origin: SignalOriginSchema.enum.SCANNER_IMPORT });
    clauses.push({ sourceProvider: SIGNAL_SOURCE_IDS.FILE_IMPORT });
  }

  const integrationProviders = thirdPartySources.filter(
    isExternalIntegrationSourceFilterValue,
  );

  if (integrationProviders.length > 0) {
    clauses.push({ sourceProvider: { in: integrationProviders } });
  }

  if (clauses.length === 0) {
    return null;
  }

  return clauses.length === 1 ? clauses[0] : { OR: clauses };
};

export const buildSignalSourceFilterWhere = ({
  sources,
}: {
  sources?: readonly SignalSourceFilterValue[];
}): Record<string, unknown> | null => {
  if (!sources?.length) {
    return null;
  }

  const originWhere = buildSignalSourceWhere({ sources });
  const providerWhere = buildSignalSourceProviderWhere({ sources });
  const clauses = [originWhere, providerWhere].filter(
    (clause): clause is Record<string, unknown> => clause != null,
  );

  if (clauses.length === 0) {
    return null;
  }

  return clauses.length === 1 ? clauses[0] : { OR: clauses };
};

export const getExternalIntegrationSourceFilterValues = ({
  sources,
}: {
  sources: readonly SignalSourceFilterValue[];
}): SignalSourceFilterValue[] =>
  sources.filter(isExternalIntegrationSourceFilterValue);

export const toDisplaySignalOrigin = <T extends string>(origin: T) =>
  origin === SignalOriginSchema.enum.SCANNER_IMPORT
    ? SignalOriginSchema.enum.EXTERNAL_INTEGRATION
    : origin;

export const expandSignalOriginFilter = ({
  origins,
}: {
  origins: readonly string[];
}): SignalOriginType[] => {
  const parsedOrigins = origins.filter(
    (origin): origin is SignalOriginType =>
      SignalOriginSchema.safeParse(origin).success,
  );

  if (
    parsedOrigins.includes(SignalOriginSchema.enum.EXTERNAL_INTEGRATION) &&
    !parsedOrigins.includes(SignalOriginSchema.enum.SCANNER_IMPORT)
  ) {
    return [...parsedOrigins, SignalOriginSchema.enum.SCANNER_IMPORT];
  }

  return parsedOrigins;
};

export const normalizeSignalOriginFilterValues = ({
  origins,
}: {
  origins: readonly string[];
}): SignalOriginType[] => {
  const uniqueOrigins = new Set<SignalOriginType>();

  origins.forEach((origin) => {
    const parsed = SignalOriginSchema.safeParse(origin);

    if (!parsed.success) {
      return;
    }

    uniqueOrigins.add(toDisplaySignalOrigin(parsed.data));
  });

  return [...uniqueOrigins];
};

export const getSourceFilterValuesForOrigins = ({
  origins,
}: {
  origins?: readonly SignalOriginType[];
}): readonly SignalSourceFilterValue[] => {
  if (!origins?.length) {
    return SIGNAL_SOURCE_FILTER_VALUES;
  }

  const hasExternalIntegrationOrigin = origins.some(isThirdPartySignalOrigin);
  const hasNativeOrigin = origins.some(
    (origin) => !isThirdPartySignalOrigin(origin),
  );

  if (hasExternalIntegrationOrigin && !hasNativeOrigin) {
    return SIGNAL_SOURCE_FILTER_VALUES.filter(
      (value) => value !== SIGNAL_SOURCE_IDS.TERRA,
    );
  }

  if (hasNativeOrigin && !hasExternalIntegrationOrigin) {
    return [SIGNAL_SOURCE_IDS.TERRA];
  }

  return SIGNAL_SOURCE_FILTER_VALUES;
};

export const constrainSourceFilterValuesToOrigins = ({
  sources,
  origins,
}: {
  sources?: readonly SignalSourceFilterValue[];
  origins?: readonly SignalOriginType[];
}): SignalSourceFilterValue[] | undefined => {
  if (!sources?.length) {
    return undefined;
  }

  const allowedSources = new Set(getSourceFilterValuesForOrigins({ origins }));
  const constrainedSources = sources.filter((source) =>
    allowedSources.has(source),
  );

  return constrainedSources.length > 0 ? constrainedSources : undefined;
};

export const resolveSourceFilterValueOnChange = ({
  isSourceControlVisible,
  selectedSources,
  currentSources,
  origins,
}: {
  isSourceControlVisible: boolean;
  selectedSources?: readonly SignalSourceFilterValue[];
  currentSources?: readonly SignalSourceFilterValue[];
  origins?: readonly SignalOriginType[];
}): SignalSourceFilterValue[] | undefined =>
  constrainSourceFilterValuesToOrigins({
    sources: isSourceControlVisible ? selectedSources : currentSources,
    origins,
  });
